"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDrafts, deleteDraft, convertDraftToSale, reset, fetchSaleDetails } from "@/state/sales/sale.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    DotsThreeVertical,
    Printer,
    Eye,
    Pencil,
    Trash,
    ArrowRight,
    Plus,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Loader2, RefreshCcw, ArrowLeft, FileCheck, ShieldAlert, Banknote, CreditCard } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import Receipt from "@/components/custom/sales/Receipt";
import { useReactToPrint } from "react-to-print";
import { useToast } from "@/components/ui/use-toast";
import { numberWithCommas } from "@/lib/formatCommas";
import { CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { todayInTimezone, daysAgoInTimezone } from "@/utils/formatDate";

export default function DraftsPage() {
    const dispatch = useDispatch();
    const { toast } = useToast();
    const { drafts, draftsLoading, loading, currentSale, success, error, message } = useSelector((state) => state.sales);
    const { business } = useSelector((state) => state.business || {});
    const { user } = useSelector((state) => state.auth);

    // Check for permission
    const hasPermission = () => {
        if (!user?.role?.permissions) return false;
        const permissions = typeof user.role.permissions === 'string'
            ? JSON.parse(user.role.permissions)
            : user.role.permissions;
        return permissions.includes('Sales');
    };

    // Dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [convertPaymentType, setConvertPaymentType] = useState(null); // 'cash' | 'credit'
    const [convertPaymentMethod, setConvertPaymentMethod] = useState("Cash");

    // Date-range filter, matching the All Sales page: two date inputs plus
    // rolling quick buttons, in the user's timezone. Defaults to today.
    const tz = user?.timezone || "UTC";
    const [startDate, setStartDate] = useState(() => todayInTimezone(tz));
    const [endDate, setEndDate] = useState(() => todayInTimezone(tz));

    // One place that knows how to (re)load the list for the active range, so
    // the mount, the refresh button, and the post-action refetch all agree.
    const reloadDrafts = useCallback(
        () => dispatch(fetchDrafts({ startDate, endDate })),
        [dispatch, startDate, endDate]
    );

    // Print Logic
    const [selectedDraftForPrint, setSelectedDraftForPrint] = useState(null);
    const receiptRef = useRef();

    const handlePrint = useReactToPrint({
        contentRef: receiptRef,
        onAfterPrint: () => setSelectedDraftForPrint(null),
        pageStyle: `
      @page { size: auto; margin: 10mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `
    });

    // Effect to trigger print when currentSale is loaded
    useEffect(() => {
        if (currentSale && selectedDraftForPrint?.id === currentSale.id) {
            setTimeout(() => {
                handlePrint();
            }, 200);
        }
    }, [currentSale, selectedDraftForPrint]);

    useEffect(() => {
        dispatch(fetchBusiness());
    }, [dispatch]);

    // Reload whenever the date range changes (and on first mount).
    useEffect(() => {
        reloadDrafts();
    }, [reloadDrafts]);

    useEffect(() => {
        if (error) {
            toast({ variant: "destructive", title: "Error", description: error });
            dispatch(reset());
        }
        if (success && message) {
            toast({ title: "Success", description: message });
            dispatch(reset());
            // Refresh list, keeping the current date range.
            reloadDrafts();
        }
    }, [error, success, message, toast, dispatch, reloadDrafts]);

    const handleRefresh = () => {
        reloadDrafts();
    };

    const handlePrintDraft = async (draft) => {
        setSelectedDraftForPrint(draft);
        try {
            await dispatch(fetchSaleDetails(draft.id)).unwrap();
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: "Failed to load draft details" });
            setSelectedDraftForPrint(null);
        }
    };

    const handleOpenDelete = (draft) => {
        setSelectedDraft(draft);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedDraft) return;
        try {
            await dispatch(deleteDraft(selectedDraft.id)).unwrap();
            setDeleteDialogOpen(false);
            setSelectedDraft(null);
        } catch (err) {
            // Error handled by useEffect
        }
    };

    const handleOpenConvert = (draft) => {
        setSelectedDraft(draft);
        setConvertPaymentType(null);
        setConvertPaymentMethod("Cash");
        setConvertDialogOpen(true);
    };

    const handleConfirmConvert = async () => {
        if (!selectedDraft || !convertPaymentType) return;
        try {
            await dispatch(convertDraftToSale({
                id: selectedDraft.id,
                paymentData: {
                    payment_type: convertPaymentType,
                    payment_method: convertPaymentType === 'cash' ? convertPaymentMethod : 'Credit',
                },
            })).unwrap();
            setConvertDialogOpen(false);
            setSelectedDraft(null);
            setConvertPaymentType(null);
            setConvertPaymentMethod("Cash");
        } catch (err) {
            // Error handled by useEffect
        }
    };

    const businessDetails = business || null;
    const currency = businessDetails?.currency || "GHS";

    // Access denied check
    if (!hasPermission()) {
        return (
            <div className="mx-auto p-4">
                <Card className="mx-auto max-w-md mt-20">
                    <CardContent className="pt-6 text-center">
                        <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-4" />
                        <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">You don't have permission to view drafts.</p>
                        <Link to="/dashboard">
                            <Button>Go to Dashboard</Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="mx-auto p-4 space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/dashboard/sales">
                        <Button variant="outline" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold">Draft Sales</h1>
                        <p className="text-sm text-muted-foreground">Manage your saved drafts</p>
                    </div>
                </div>

                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleRefresh} disabled={draftsLoading}>
                        <RefreshCcw className={`h-4 w-4 mr-2 ${draftsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Link to="/dashboard/sales/create">
                        <Button>
                            <Plus className="mr-2 h-4 w-4" weight="bold" /> New Sale
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Date filter — same pattern as the All Sales page */}
            <Card className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                    {/* Date Range */}
                    <div className="flex items-center gap-1 bg-muted/30 px-2 rounded-md border">
                        <Input
                            type="date"
                            value={startDate}
                            max={endDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
                        />
                        <span className="text-muted-foreground text-xs">to</span>
                        <Input
                            type="date"
                            value={endDate}
                            min={startDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
                        />
                    </div>

                    {/* Quick Date Filters */}
                    <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            const today = todayInTimezone(tz);
                            setStartDate(today); setEndDate(today);
                        }}>Today</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            setStartDate(daysAgoInTimezone(1, tz)); setEndDate(daysAgoInTimezone(1, tz));
                        }}>Yesterday</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            setStartDate(daysAgoInTimezone(7, tz)); setEndDate(todayInTimezone(tz));
                        }}>Week</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            setStartDate(daysAgoInTimezone(30, tz)); setEndDate(todayInTimezone(tz));
                        }}>Month</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            setStartDate(daysAgoInTimezone(365, tz)); setEndDate(todayInTimezone(tz));
                        }}>Year</Button>
                    </div>
                </div>
            </Card>

            {/* Drafts Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Card className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800">
                    <p className="text-xs text-indigo-600 dark:text-indigo-400">Total Drafts</p>
                    <p className="text-xl font-bold text-indigo-900 dark:text-indigo-100">{drafts?.length || 0}</p>
                </Card>
                <Card className="p-3 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-600 dark:text-blue-400">Total Value</p>
                    <p className="text-xl font-bold text-blue-900 dark:text-blue-100">
                        {currency} {numberWithCommas((drafts?.reduce((sum, d) => sum + parseFloat(d.total || 0), 0) || 0).toFixed(2))}
                    </p>
                </Card>
            </div>

            <Card className="enterprise-table-container p-0">
                <div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Draft #</TableHead>
                                <TableHead>Customer</TableHead>
                                <TableHead>Items</TableHead>
                                <TableHead>Total</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {draftsLoading && drafts?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="h-24 text-center">
                                        <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" /> Loading drafts...</span>
                                    </TableCell>
                                </TableRow>
                            ) : drafts?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center gap-2">
                                            <p>No drafts found for the selected dates.</p>
                                            <Link to="/dashboard/sales/create">
                                                <Button size="sm">Create New Sale</Button>
                                            </Link>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                drafts?.map((draft) => (
                                    <TableRow key={draft.id}>
                                        <TableCell>{format(new Date(draft.createdAt), "dd MMM yyyy HH:mm")}</TableCell>
                                        <TableCell className="font-mono">{draft.order_number}</TableCell>
                                        <TableCell>{draft.Customer?.name || draft.billing_fname || "Walk-in"}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{draft.SaleItems?.length || 0} items</Badge>
                                        </TableCell>
                                        <TableCell className="font-bold">{currency} {parseFloat(draft.total).toFixed(2)}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="bg-slate-100">{draft.type?.toUpperCase()}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                                        <DotsThreeVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                    <DropdownMenuItem asChild>
                                                        <Link to={`/dashboard/sales/create?draft=${draft.id}`}>
                                                            <Pencil className="mr-2 h-4 w-4" /> Edit Draft
                                                        </Link>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handlePrintDraft(draft)}>
                                                        <Printer className="mr-2 h-4 w-4" /> Print Draft
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => handleOpenConvert(draft)} className="text-green-600">
                                                        <FileCheck className="mr-2 h-4 w-4" /> Complete Sale
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleOpenDelete(draft)} className="text-red-600">
                                                        <Trash className="mr-2 h-4 w-4" /> Delete Draft
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Draft</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete draft {selectedDraft?.order_number}? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleConfirmDelete} disabled={loading}>
                            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Convert to Sale Dialog with Cash/Credit Selection */}
            <Dialog open={convertDialogOpen} onOpenChange={(open) => {
                setConvertDialogOpen(open);
                if (!open) {
                    setConvertPaymentType(null);
                    setConvertPaymentMethod("Cash");
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Complete Sale</DialogTitle>
                        <DialogDescription>
                            Convert draft {selectedDraft?.order_number} to a completed sale. Stock will be deducted for all items.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Sale Summary */}
                    <div className="rounded-lg border bg-muted/50 p-3 space-y-1">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Total Amount:</span>
                            <span className="text-lg font-bold">{currency} {numberWithCommas(parseFloat(selectedDraft?.total || 0).toFixed(2))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Items:</span>
                            <span>{selectedDraft?.SaleItems?.length || 0} items</span>
                        </div>
                    </div>

                    {/* Payment Type Selection */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">How is the customer paying?</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setConvertPaymentType("cash")}
                                className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                                    convertPaymentType === "cash"
                                        ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                                        : "border-muted hover:border-muted-foreground/30"
                                }`}
                            >
                                <Banknote className={`h-6 w-6 ${convertPaymentType === "cash" ? "text-green-600" : "text-muted-foreground"}`} />
                                <span className={`text-sm font-medium ${convertPaymentType === "cash" ? "text-green-700 dark:text-green-300" : ""}`}>Cash Sale</span>
                                <span className="text-xs text-muted-foreground">Paid in full now</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setConvertPaymentType("credit")}
                                className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                                    convertPaymentType === "credit"
                                        ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20"
                                        : "border-muted hover:border-muted-foreground/30"
                                }`}
                            >
                                <CreditCard className={`h-6 w-6 ${convertPaymentType === "credit" ? "text-amber-600" : "text-muted-foreground"}`} />
                                <span className={`text-sm font-medium ${convertPaymentType === "credit" ? "text-amber-700 dark:text-amber-300" : ""}`}>Credit Sale</span>
                                <span className="text-xs text-muted-foreground">Pay later</span>
                            </button>
                        </div>
                    </div>

                    {/* Payment Method - only shown for cash sales */}
                    {convertPaymentType === "cash" && (
                        <div className="space-y-2">
                            <Label>Payment Method</Label>
                            <Select value={convertPaymentMethod} onValueChange={setConvertPaymentMethod}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Cash">Cash</SelectItem>
                                    <SelectItem value="Momo">Mobile Money</SelectItem>
                                    <SelectItem value="Card">Card</SelectItem>
                                    <SelectItem value="Check">Check</SelectItem>
                                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmConvert} disabled={loading || !convertPaymentType}>
                            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileCheck className="h-4 w-4 mr-2" />}
                            {convertPaymentType === "cash" ? "Complete & Record Payment" : convertPaymentType === "credit" ? "Complete as Credit" : "Complete Sale"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Hidden Receipt Component for Printing */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <Receipt
                    ref={receiptRef}
                    sale={currentSale}
                    businessDetails={businessDetails}
                    paymentStatus="draft"
                />
            </div>
        </div>
    );
}
